import { yupResolver } from '@hookform/resolvers/yup';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, DialogActions, DialogContent, DialogTitle, IconButton, useTheme } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import {
  type Control, type FieldValues, type Resolver,
  useForm, useWatch,
} from 'react-hook-form';

import useSnackBar from '../../hooks/use-snack-bar.js';
import { humanize } from '../../utils/index.js';


interface FormDialogProps<T extends FieldValues, U extends FieldValues> {
  open: boolean;
  name: string;
  onClose: () => void;
  isEditing?: boolean;
  children: (control: Control<T>, errors: any, watchedFields: any) => React.ReactNode;
  data: T;
  schema: Parameters<typeof yupResolver<U>>[0];
  onSubmit: (data: T, isEditing: boolean) => Promise<void>;
  refetch: () => void;
  watch?: keyof T | Array<keyof T>;
}

const FormDialog = <T extends FieldValues, U extends FieldValues>({
                                                open,
                                                isEditing = false,
                                                name,
                                                onClose,
                                                data,
                                                schema,
                                                onSubmit,
                                                refetch,
                                                children,
                                                watch,
                                              }: FormDialogProps<T, U>) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const snackBar = useSnackBar();
  const theme = useTheme();
  const {
    control,
    handleSubmit,
    reset,
    formState: {errors, isDirty},
  } = useForm<T>({
    values: data,
    resolver: yupResolver(schema) as unknown as Resolver<T>,
  });

  const watchedFields = useWatch<T>({
    control,
    ...(watch && {name: watch}),
    disabled: !watch,
  });

  const resetForm = () => {
    reset();
  };

  const handleClosing = () => {
    onClose();
    setTimeout(() => setIsSubmitting(false), theme.transitions.duration.leavingScreen);
    resetForm();
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isDirty) {
          handleClosing();
        }
      }}
      PaperProps={{
        component: 'form',
        sx: {minWidth: 450},
        onSubmit: handleSubmit(async (data) => {
          setIsSubmitting(true);
          try {
            await onSubmit(data, isEditing);
            snackBar.success(`${humanize(name)} ${isEditing ? 'updated' : 'created'} successfully!`);
            refetch();
          } catch (e) {
            snackBar.error(`${humanize(name)} ${isEditing ? 'update' : 'creation'} failed!`);
          }
          handleClosing();
        }),
      }}
    >
      <DialogTitle>
        {isEditing ? 'Update' : 'Add a new'} {humanize(name)}
        {isEditing ? (
          <IconButton onClick={resetForm} disabled={!isDirty}>
            <RestartAltIcon/>
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent>
        {children(control, errors, watchedFields)}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          type={'submit'}
          loading={isSubmitting}
          variant={'contained'}
          disabled={!_.isEmpty(errors)}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default FormDialog;
